import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Brain, Crown, Rocket, Heart, Cpu, CheckCircle, Sparkles, Target, TrendingUp, Zap, Quote, Clock, Award } from 'lucide-react';
import { GlowCard } from '../components/GlowCard';
import { AnimatedCounter, ROICalculator } from '../components/GrowthWidgets';
import { getSystemBySlug, growthSystems, methodology, caseStudies } from '../data/servicesData';

const iconMap = {
  Brain: Brain,
  Crown: Crown,
  Rocket: Rocket,
  Heart: Heart,
  Cpu: Cpu,
};

const colorMap = {
  violet: { gradient: 'from-violet-500 to-purple-600', text: 'text-violet-400', bg: 'bg-violet-500/20' },
  emerald: { gradient: 'from-emerald-500 to-teal-600', text: 'text-emerald-400', bg: 'bg-emerald-500/20' },
  pink: { gradient: 'from-pink-500 to-rose-600', text: 'text-pink-400', bg: 'bg-pink-500/20' },
  amber: { gradient: 'from-amber-500 to-orange-600', text: 'text-amber-400', bg: 'bg-amber-500/20' },
  cyan: { gradient: 'from-cyan-500 to-blue-600', text: 'text-cyan-400', bg: 'bg-cyan-500/20' },
};

export default function SystemDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const system = getSystemBySlug(slug);
  const caseStudy = system ? caseStudies[system.id] : null;

  if (!system) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-32 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Sistem bulunamadı</h1>
          <Link to="/buyume-sistemleri" className="text-violet-400 hover:text-violet-300">
            ← Büyüme Sistemlerine Dön
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = iconMap[system.icon];
  const colors = colorMap[system.color];

  // Find adjacent systems for navigation
  const currentIndex = growthSystems.findIndex(s => s.slug === slug);
  const prevSystem = currentIndex > 0 ? growthSystems[currentIndex - 1] : null;
  const nextSystem = currentIndex < growthSystems.length - 1 ? growthSystems[currentIndex + 1] : null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-32" data-testid="system-detail-page">
      {/* Back Navigation */}
      <div className="container-wide py-4">
        <Link 
          to="/buyume-sistemleri" 
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Büyüme Sistemleri
        </Link>
      </div>

      {/* Hero Section */}
      <section className="section-padding pt-8">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Badge */}
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colors.gradient} p-0.5`}>
                  <div className="w-full h-full rounded-2xl bg-[#0A0A0A] flex items-center justify-center">
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>
                </div>
                {system.isNew && (
                  <span className="px-3 py-1 text-xs font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full">
                    YENİ
                  </span>
                )}
              </div>

              <span className={`text-xs font-mono ${colors.text} tracking-widest uppercase mb-2 block`}>
                {system.aka}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                {system.name}
              </h1>
              <p className="text-xl text-zinc-400 leading-relaxed mb-8">
                {system.whatItIs}
              </p>

              <Link 
                to="/iletisim" 
                className="btn-gradient inline-flex items-center gap-2"
              >
                Bu Sistemi Konuşalım
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <GlowCard className="p-8">
                <h3 className="text-lg font-semibold text-white mb-6">Bu Sistem Kimler İçin?</h3>
                <ul className="space-y-4">
                  {system.bestFor.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className={`w-6 h-6 rounded-full ${colors.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <CheckCircle className={`w-3.5 h-3.5 ${colors.text}`} />
                      </div>
                      <span className="text-zinc-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </GlowCard>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem & Solution Section */}
      <section className="section-padding bg-[#121212]">
        <div className="container-wide">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Problem */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="h-full p-8 rounded-2xl border border-red-500/20 bg-red-500/5">
                <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center mb-6">
                  <Target className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Problem</h3>
                <p className="text-zinc-400 leading-relaxed mb-6">{system.problem}</p>
                <div className="pt-4 border-t border-red-500/10">
                  <h4 className="text-sm font-semibold text-red-400 mb-2">Neden Klasik Yöntemler Yetmez?</h4>
                  <p className="text-zinc-500 text-sm">{system.whyTraditionalFails}</p>
                </div>
              </div>
            </motion.div>

            {/* Solution */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className={`h-full p-8 rounded-2xl border border-emerald-500/20 bg-emerald-500/5`}>
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-6">
                  <Zap className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Medyatik Çözümü</h3>
                <p className="text-zinc-400 leading-relaxed">{system.whatItIs}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className={`text-xs font-mono ${colors.text} tracking-widest uppercase mb-4 block`}>
              Modüller
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Sistem Bileşenleri
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {system.modules.map((module, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <GlowCard className="p-6 h-full">
                  <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center mb-4`}>
                    <span className={`text-lg font-bold ${colors.text}`}>{String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{module.name}</h3>
                  <p className="text-zinc-400 text-sm">{module.description}</p>
                </GlowCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* aifabric Integration */}
      <section className="section-padding bg-[#121212]">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-cyan-900/20 via-[#0A0A0A] to-violet-900/20 border border-cyan-500/20 p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <Sparkles className="w-8 h-8 text-cyan-400" />
                    <span className="text-xl font-bold text-white">aifabric Entegrasyonu</span>
                  </div>
                  <p className="text-zinc-400 leading-relaxed mb-6">
                    Bu sistemde aifabric yapay zeka katmanı şu yetenekleri sağlar:
                  </p>
                  <ul className="space-y-3">
                    {system.aifabricIntegration.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                        <span className="text-zinc-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex items-center justify-center">
                  <div className="w-48 h-48 rounded-full bg-gradient-to-br from-cyan-500/20 to-violet-500/20 flex items-center justify-center">
                    <Sparkles className="w-24 h-24 text-cyan-400/50" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Case Study / Success Story */}
      {caseStudy && (
        <section className="section-padding bg-[#0A0A0A]">
          <div className="container-wide">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <span className="text-xs font-mono text-amber-400 tracking-widest uppercase mb-4 block">
                Başarı Hikayesi
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Gerçek Sonuçlar
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-amber-900/20 via-[#121212] to-orange-900/20 border border-amber-500/20 p-8 md:p-12">
                <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 rounded-full">
                  <Clock className="w-4 h-4 text-amber-400" />
                  <span className="text-xs text-amber-400 font-medium">{caseStudy.duration}</span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                  {/* Left: Story */}
                  <div>
                    <div className="flex items-center gap-3 mb-6">
                      <Award className="w-8 h-8 text-amber-400" />
                      <div>
                        <h3 className="text-xl font-bold text-white">{caseStudy.title}</h3>
                        <p className="text-sm text-zinc-500">{caseStudy.client}</p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <h4 className="text-sm font-semibold text-red-400 mb-2">Zorluk</h4>
                        <p className="text-zinc-400">{caseStudy.challenge}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-emerald-400 mb-2">Çözüm</h4>
                        <p className="text-zinc-400">{caseStudy.solution}</p>
                      </div>
                    </div>

                    {/* Quote */}
                    <div className="mt-8 p-6 bg-zinc-900/50 rounded-xl border-l-4 border-amber-500">
                      <Quote className="w-6 h-6 text-amber-400/50 mb-3" />
                      <p className="text-white italic">"{caseStudy.quote}"</p>
                    </div>
                  </div>

                  {/* Right: Results with Animated Numbers */}
                  <div className="flex flex-col justify-center">
                    <h4 className="text-sm font-semibold text-zinc-500 uppercase tracking-wider mb-6 text-center lg:text-left">
                      Sonuçlar
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {caseStudy.results.map((result, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          className="p-6 bg-zinc-900/80 rounded-2xl text-center border border-zinc-800 hover:border-amber-500/30 transition-colors"
                        >
                          <div className="text-3xl md:text-4xl font-bold text-amber-400 mb-2">
                            <AnimatedCounter value={result.value} duration={2.5} />
                          </div>
                          <div className="text-sm text-zinc-400">{result.label}</div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ROI Calculator Section */}
      <section className="section-padding bg-[#121212]">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-xs font-mono text-emerald-400 tracking-widest uppercase mb-4 block">
              Potansiyel Hesaplama
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Büyüme Potansiyelinizi Görün
            </h2>
          </motion.div>
          
          <div className="max-w-xl mx-auto">
            <ROICalculator />
          </div>
        </div>
      </section>

      {/* Deliverables & Metrics */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="container-wide">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Deliverables */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center`}>
                  <CheckCircle className={`w-5 h-5 ${colors.text}`} />
                </div>
                Teslim Edilecekler
              </h3>
              <ul className="space-y-4">
                {system.deliverables.map((item, index) => (
                  <li key={index} className="flex items-start gap-4 p-4 bg-zinc-900/50 rounded-xl border border-zinc-800/50">
                    <span className={`text-sm font-mono ${colors.text}`}>{String(index + 1).padStart(2, '0')}</span>
                    <span className="text-zinc-300">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Success Metrics */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h3 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                </div>
                Başarı Metrikleri
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {system.successMetrics.map((item, index) => (
                  <div key={index} className="p-6 bg-zinc-900/50 rounded-xl border border-zinc-800/50 text-center">
                    <div className="text-2xl font-bold text-emerald-400 mb-2">{item.metric}</div>
                    <div className="text-sm text-zinc-500">{item.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Methodology Signature */}
      <section className="section-padding bg-[#121212]">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="methodology-card max-w-4xl mx-auto">
              <div className="relative z-10 text-center">
                <span className="text-xs font-mono text-violet-400 tracking-widest uppercase mb-4 block">
                  İmza Katmanı
                </span>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  {methodology.name}
                </h3>
                <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
                  {methodology.experienceYears} yıllık deneyimle geliştirilen metodoloji, 
                  bu sistemin her aşamasına entegre edilmiştir.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {methodology.pillars.map((pillar, index) => (
                    <div key={index} className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800/50">
                      <div className="text-sm font-medium text-white mb-1">{pillar.title}</div>
                      <div className="text-xs text-zinc-500">{pillar.description}</div>
                    </div>
                  ))}
                </div>
                <Link 
                  to="/metodoloji" 
                  className="inline-flex items-center gap-2 mt-8 text-violet-400 hover:text-violet-300 transition-colors"
                >
                  Metodolojiyi Keşfet
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Bu Sistemi Projenizde Kullanın
            </h2>
            <p className="text-zinc-400 text-lg mb-10">
              15 dakikalık ücretsiz keşif görüşmesinde, {system.name} sisteminin 
              sizin için nasıl çalışacağını konuşalım.
            </p>
            <Link 
              to="/iletisim" 
              className="btn-gradient inline-flex items-center gap-2"
            >
              Görüşme Talep Et
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-8 bg-[#121212] border-t border-zinc-800">
        <div className="container-wide">
          <div className="flex justify-between items-center">
            {prevSystem ? (
              <Link 
                to={`/buyume-sistemleri/${prevSystem.slug}`}
                className="flex items-center gap-3 text-zinc-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <div>
                  <span className="text-xs text-zinc-500 block">Önceki</span>
                  <span className="text-sm font-medium">{prevSystem.name}</span>
                </div>
              </Link>
            ) : <div />}
            
            {nextSystem ? (
              <Link 
                to={`/buyume-sistemleri/${nextSystem.slug}`}
                className="flex items-center gap-3 text-zinc-400 hover:text-white transition-colors text-right"
              >
                <div>
                  <span className="text-xs text-zinc-500 block">Sonraki</span>
                  <span className="text-sm font-medium">{nextSystem.name}</span>
                </div>
                <ArrowRight className="w-5 h-5" />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>
    </div>
  );
}
